import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  PhotoIcon,
  SparklesIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  EyeIcon,
  PlusIcon,
  MinusIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { aiVisualizationOperations, supabase } from '../utils/supabase';
import FalAIService from '../utils/falAI';

const AIVisualizationModal = ({ isOpen, onClose, product }) => {
  const [step, setStep] = useState('select'); // 'select', 'upload', 'generating', 'result'
  const [selectedProducts, setSelectedProducts] = useState(product ? [product] : []);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [userImage, setUserImage] = useState(null);
  const [userImageFile, setUserImageFile] = useState(null);
  const [generatedImages, setGeneratedImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (file) => {
    if (file && file.type.startsWith('image/')) {
      setUserImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setUserImage(e.target.result);
        setError(null);
      };
      reader.readAsDataURL(file);
    } else {
      setError('Lütfen geçerli bir görsel dosyası seçin');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (files[0]) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  // Ürünleri yükle
  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    try {
      const { data: products, error } = await supabase
        .from('products')
        .select('id, name, image_url, ai_images, ai_image_url, price')
        .eq('is_active', true);
      
      if (error) throw error;
      
      // Frontend'de AI görseli olan ürünleri filtrele
      const productsWithAI = (products || []).filter(product => {
        return (product.ai_images && Array.isArray(product.ai_images) && product.ai_images.length > 0) ||
               (product.ai_image_url && product.ai_image_url.trim() !== '');
      });
      
      setAvailableProducts(productsWithAI);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError('Ürünler yüklenirken hata oluştu');
    }
  };

  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const isSelected = prev.find(p => p.id === product.id);
      if (isSelected) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  const generateVisualization = async () => {
    if (!userImageFile || selectedProducts.length === 0) {
      setError('Görsel ve en az bir ürün seçimi gerekli');
      return;
    }

    setLoading(true);
    setStep('generating');
    setError(null);
    setGeneratedImages([]);

    try {
      // 1. Kullanıcının görselini Supabase'e yükle
      console.log('Uploading user image...');
      const uploadResult = await aiVisualizationOperations.uploadUserImage(
        'temp_user_' + Date.now(),
        userImageFile
      );

      if (!uploadResult.success) {
        throw new Error('Görsel yüklenirken hata: ' + uploadResult.error);
      }

      console.log('User image uploaded:', uploadResult.imageUrl);

      // 2. Her seçilen ürün için AI görselleştirme yap
      const results = [];
      for (let i = 0; i < selectedProducts.length; i++) {
        const product = selectedProducts[i];
        console.log(`Processing product ${i + 1}/${selectedProducts.length}: ${product.name}`);
        
        // Ürünün tüm AI görsellerini al
        const productImagesResult = await aiVisualizationOperations.getProductAIImages(product.id);
        
        if (!productImagesResult.success || !productImagesResult.images || productImagesResult.images.length === 0) {
          console.warn(`AI görseli bulunamadı: ${product.name}`);
          results.push({
            product: product,
            error: 'Bu ürün için AI görseli bulunamadı',
            success: false
          });
          continue;
        }

        console.log(`Product AI images for ${product.name}:`, productImagesResult.images);

        // Her AI görseli için ayrı yerleştirme yap
        const productResults = [];
        for (let j = 0; j < productImagesResult.images.length; j++) {
          const aiImage = productImagesResult.images[j];
          
          try {
            // 3. fal.ai ile görselleştirme yap
            const aiResult = await FalAIService.generateVisualization(
              aiImage.url,
              uploadResult.imageUrl
            );

            if (aiResult.success && aiResult.data?.images?.length > 0) {
              productResults.push({
                aiImageUrl: aiImage.url,
                generatedImageUrl: aiResult.data.images[0].url,
                success: true,
                isPrimary: aiImage.is_primary || false
              });
              
              // Optional: Save to history
              try {
                await aiVisualizationOperations.saveVisualizationHistory(
                  'temp_user_' + Date.now(),
                  product.id,
                  uploadResult.imageUrl,
                  aiResult.data.images[0].url
                );
              } catch (historyError) {
                console.warn('Could not save to history:', historyError);
              }
            } else {
              productResults.push({
                aiImageUrl: aiImage.url,
                error: aiResult.error || 'Görsel oluşturulamadı',
                success: false,
                isPrimary: aiImage.is_primary || false
              });
            }
          } catch (error) {
            console.error('AI generation error:', error);
            productResults.push({
              aiImageUrl: aiImage.url,
              error: error.message || 'Görsel oluşturulamadı',
              success: false,
              isPrimary: aiImage.is_primary || false
            });
          }
        }
        
        results.push({
          product: product,
          results: productResults,
          success: productResults.some(r => r.success)
        });
      }

      setGeneratedImages(results);
      
      if (results.length > 0) {
        setStep('result');
      } else {
        throw new Error('Hiçbir ürün için görsel oluşturulamadı');
      }

    } catch (error) {
      console.error('Visualization generation error:', error);
      setError(error.message);
      setStep('upload');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setStep(product ? 'upload' : 'select');
    setSelectedProducts(product ? [product] : []);
    setUserImage(null);
    setUserImageFile(null);
    setGeneratedImages([]);
    setError(null);
    setLoading(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-emerald-600 to-green-500 text-white p-6">
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
            
            <div className="flex items-center space-x-3">
              <SparklesIcon className="h-8 w-8" />
              <div>
                <h2 className="text-2xl font-bold">Evinde Gör</h2>
                <p className="text-emerald-100">
                  {selectedProducts.length === 1 
                    ? `${selectedProducts[0].name} ürününü evinizde nasıl görüneceğini keşfedin`
                    : `${selectedProducts.length} ürünü evinizde nasıl görüneceğini keşfedin`
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            {step === 'select' && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Hangi ürünleri evinizde görmek istiyorsunuz?
                  </h3>
                  <p className="text-gray-600">
                    Birden fazla ürün seçebilir ve aynı odaya yerleştirebilirsiniz
                  </p>
                </div>

                {/* Seçilen Ürünler Özeti */}
                {selectedProducts.length > 0 && (
                  <div className="bg-emerald-50 rounded-2xl p-4">
                    <h4 className="font-semibold text-emerald-800 mb-2">
                      Seçilen Ürünler ({selectedProducts.length})
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProducts.map(product => (
                        <span key={product.id} className="bg-emerald-200 text-emerald-800 px-3 py-1 rounded-full text-sm font-medium">
                          {product.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Ürün Listesi */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                  {availableProducts.map(product => {
                    const isSelected = selectedProducts.find(p => p.id === product.id);
                    return (
                      <motion.div
                        key={product.id}
                        whileHover={{ scale: 1.02 }}
                        className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-emerald-500 bg-emerald-50' 
                            : 'border-gray-200 bg-white hover:border-emerald-300'
                        }`}
                        onClick={() => toggleProductSelection(product)}
                      >
                        {/* Seçim İkonu */}
                        <div className={`absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          isSelected 
                            ? 'bg-emerald-500 border-emerald-500 text-white' 
                            : 'border-gray-300 bg-white'
                        }`}>
                          {isSelected ? <CheckIcon className="h-4 w-4" /> : <PlusIcon className="h-4 w-4 text-gray-400" />}
                        </div>

                        {/* Ürün Görseli */}
                        <img
                          src={product.image_url || '/images/koltuk.jpg'}
                          alt={product.name}
                          className="w-full h-24 object-cover rounded-lg mb-3"
                        />
                        
                        {/* Ürün Bilgileri */}
                        <h4 className="font-semibold text-gray-900 text-sm mb-1 truncate">
                          {product.name}
                        </h4>
                        <p className="text-emerald-600 font-medium text-sm">
                          ₺{product.price?.toLocaleString()}
                        </p>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Devam Butonu */}
                {selectedProducts.length > 0 && (
                  <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={() => setStep('upload')}
                    className="w-full bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-700 hover:to-green-600 text-white font-semibold py-4 rounded-xl transition-all flex items-center justify-center space-x-2"
                  >
                    <span>Oda Fotoğrafını Yükle ({selectedProducts.length} ürün)</span>
                    <ArrowPathIcon className="h-5 w-5" />
                  </motion.button>
                )}
              </div>
            )}

            {step === 'upload' && (
              <div className="space-y-6">
                {/* Selected Products Info */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Seçilen Ürünler ({selectedProducts.length})
                    </h3>
                    <button
                      onClick={() => setStep('select')}
                      className="text-emerald-600 hover:text-emerald-700 font-medium text-sm"
                    >
                      Değiştir
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedProducts.map(product => (
                      <div key={product.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                        <img
                          src={product.image_url || '/images/koltuk.jpg'}
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm truncate">{product.name}</h4>
                          <p className="text-emerald-600 text-sm">₺{product.price?.toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Upload Area */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Evinizin fotoğrafını yükleyin
                  </h3>
                  
                  <div
                    className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
                      dragOver
                        ? 'border-emerald-500 bg-emerald-50'
                        : userImage
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-300 bg-gray-50 hover:border-emerald-400 hover:bg-emerald-50'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                  >
                    {userImage ? (
                      <div className="space-y-4">
                        <img
                          src={userImage}
                          alt="Yüklenen görsel"
                          className="w-full max-h-64 object-contain rounded-xl mx-auto"
                        />
                        <div className="flex items-center justify-center space-x-4">
                          <span className="text-green-600 font-semibold flex items-center">
                            <PhotoIcon className="h-5 w-5 mr-2" />
                            Görsel başarıyla yüklendi
                          </span>
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            className="text-emerald-600 hover:text-emerald-700 font-medium"
                          >
                            Değiştir
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <CloudArrowUpIcon className="h-16 w-16 text-gray-400 mx-auto" />
                        <div>
                          <p className="text-lg font-semibold text-gray-900 mb-2">
                            Görseli buraya sürükleyin veya tıklayın
                          </p>
                          <p className="text-gray-600">
                            JPG, PNG, WEBP formatları desteklenir. Maksimum 10MB.
                          </p>
                        </div>
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors"
                        >
                          Dosya Seç
                        </button>
                      </div>
                    )}
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-red-600">{error}</p>
                  </div>
                )}

                {/* Generate Button */}
                {userImage && selectedProducts.length > 0 && (
                  <motion.button
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    onClick={generateVisualization}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-700 hover:to-green-600 text-white font-semibold py-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    <SparklesIcon className="h-5 w-5" />
                    <span>AI ile Görselleştir ({selectedProducts.length} ürün)</span>
                  </motion.button>
                )}
              </div>
            )}

            {step === 'generating' && (
              <div className="flex flex-col items-center justify-center py-16 space-y-6">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-green-500 rounded-full flex items-center justify-center"
                >
                  <SparklesIcon className="h-8 w-8 text-white" />
                </motion.div>
                
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    AI Görselleştirme Yapılıyor
                  </h3>
                  <p className="text-gray-600">
                    Bu işlem 30-60 saniye sürebilir. Lütfen bekleyin...
                  </p>
                </div>

                {/* Progress Steps */}
                <div className="w-full max-w-md space-y-3">
                  {[
                    'Görsel yükleniyor...',
                    `${selectedProducts.length} ürün görseli alınıyor...`,
                    'AI analiz yapıyor...',
                    'Sonuçlar oluşturuluyor...'
                  ].map((text, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.5 }}
                      className="flex items-center space-x-3"
                    >
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      <span className="text-gray-600">{text}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {step === 'result' && generatedImages.length > 0 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Harika! {generatedImages.filter(img => img.success).length} ürün evinize yerleştirildi:
                  </h3>
                  <p className="text-gray-600">
                    AI teknolojisi ile {generatedImages.reduce((total, result) => total + (result.results?.filter(r => r.success).length || 0), 0)} görsel varyasyonu oluşturuldu
                  </p>
                </div>

                {/* Results Grid */}
                <div className="space-y-8">
                  {generatedImages.map((result, index) => (
                    <div key={result.product.id} className="space-y-4">
                      {/* Product Name */}
                      <div className="text-center border-b border-gray-200 pb-2">
                        <h4 className="font-semibold text-lg text-gray-900">
                          {result.product.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {result.results?.length > 0 ? `${result.results.length} AI görsel varyasyonu` : 'Görsel bulunamadı'}
                        </p>
                      </div>
                      
                      {result.success ? (
                        /* Success - Show All Generated Images */
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {result.results.filter(r => r.success).map((aiResult, aiIndex) => (
                            <div key={aiIndex} className="space-y-2">
                              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                                <img
                                  src={aiResult.generatedImageUrl}
                                  alt={`AI Generated Result ${aiIndex + 1} for ${result.product.name}`}
                                  className="w-full h-auto"
                                />
                                
                                {/* AI Badge */}
                                <div className="absolute top-2 left-2 bg-black/70 backdrop-blur-sm px-2 py-1 rounded-lg">
                                  <div className="flex items-center space-x-1">
                                    <SparklesIcon className="h-3 w-3 text-emerald-400" />
                                    <span className="text-xs font-semibold text-white">
                                      AI #{aiIndex + 1}
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Primary Badge */}
                                {aiResult.isPrimary && (
                                  <div className="absolute top-2 right-2 bg-emerald-600/90 backdrop-blur-sm px-2 py-1 rounded-lg">
                                    <span className="text-xs font-semibold text-white">
                                      Ana Görsel
                                    </span>
                                  </div>
                                )}
                              </div>
                              
                              {/* Variation Info */}
                              <p className="text-xs text-gray-500 text-center">
                                Varyasyon {aiIndex + 1}{aiResult.isPrimary ? ' (Ana)' : ''}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        /* Error - Show Error Message */
                        <div className="relative rounded-2xl border-2 border-red-200 bg-red-50 p-6 text-center">
                          <div className="text-red-600 mb-2">
                            <SparklesIcon className="h-8 w-8 mx-auto" />
                          </div>
                          <p className="text-red-600 font-medium text-sm">
                            Bu ürün için görsel oluşturulamadı
                          </p>
                          <p className="text-red-500 text-xs mt-1">
                            {result.error || (result.results && result.results.length > 0 ? result.results[0].error : 'Bilinmeyen hata')}
                          </p>
                        </div>
                      )}
                      
                      {/* Show failed attempts */}
                      {result.results && result.results.some(r => !r.success) && (
                        <div className="mt-2">
                          <p className="text-xs text-amber-600">
                            ⚠️ {result.results.filter(r => !r.success).length} görsel varyasyonu oluşturulamadı
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={resetModal}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 rounded-xl transition-colors flex items-center justify-center space-x-2"
                  >
                    <ArrowPathIcon className="h-5 w-5" />
                    <span>Yeni Deneme</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      // Navigate to products page
                      window.open('/products', '_blank');
                    }}
                    className="flex-1 bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-700 hover:to-green-600 text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center space-x-2"
                  >
                    <EyeIcon className="h-5 w-5" />
                    <span>Ürünleri İncele</span>
                  </button>
                </div>
              </div>
            )}
            
            {/* Error Message */}
            {error && (
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-600">{error}</p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AIVisualizationModal;
